// lib/v2-path.ts — build /v2/* path strings from key/value maps.
//
// The v2 convention (AGENTS.md §1.5) is: every parameter becomes a path
// segment, not a query string. This helper reads a plain opts object
// `{ year, cnpj, sector, limit, ... }` and returns a path like
// `/v2/intel/financials/ratios/year/2024/cnpj/.../limit/100`.
//
// Skipped segments (undefined / empty) are simply omitted. CNPJ is
// normalised to digits-only so a formatted input doesn't break the
// segment matcher.
import { toDigitsCnpj } from "@shared/utils/cnpj";

export interface V2PathOpts {
  year?: number | string;
  cnpj?: string;
  sector?: string;
  segment?: string;
  limit?: number;
  q?: string;
  window_days?: number;
  min_degree?: number;
  category?: string;
  since?: string;
  n?: number;
  [key: string]: unknown;
}

function normalise(value: unknown): string | undefined {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return undefined;
    return String(value);
  }
  const str = String(value);
  return str.length === 0 ? undefined : str;
}

/**
 * Build a path with optional key/value segments. Returns the prefix
 * unchanged when no segments resolve. Each segment is appended as
 * `/key/value`; values are URL-encoded so the segment matcher doesn't
 * break on spaces or slashes.
 */
export function v2Path(prefix: string, opts: V2PathOpts = {}): string {
  const segments: string[] = [];
  for (const [key, raw] of Object.entries(opts)) {
    let value = normalise(raw);
    if (value === undefined) continue;
    if (key === "cnpj") {
      value = toDigitsCnpj(value) ?? value;
    }
    // Don't encode — Elysia matches literal segments. Slashes in the
    // value would split the path, so callers must pre-strip them.
    segments.push(encodeURIComponent(key));
    segments.push(encodeURIComponent(value));
  }
  return segments.length === 0 ? prefix : `${prefix}/${segments.join("/")}`;
}
